import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import ExpiredSalesPanel from "@/components/ExpiredSalesPanel";
import SizePricePanel from "@/components/SizePricePanel";
import { PromoMessageInput } from "@/components/PromoMessage";
import { FailedSitesNotice, LoadingSitesNotice, usePollWhileLoading, NeverRunsWarning } from "@/components/rowSelection";
import { Search, Loader2, UploadCloud, Layers, Percent, DollarSign, Store, RotateCcw, CalendarClock, Plus, Trash2, Clock, Calendar, Pencil } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 25 }, (_, i) => i);

export default function MasterCatalog({ sites }) {
  const [selected, setSelected] = useState(() => sites.map((s) => s.id));

  useEffect(() => {
    setSelected(sites.map((s) => s.id));
  }, [sites]);

  const toggleSite = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-xl font-semibold">Master Catalog — edit once, apply to every site</h3>
        <p className="text-sm text-muted-foreground">
          Change a price, run a tag promotion, or edit add-ons across all your connected WooCommerce URLs at once.
        </p>
      </div>

      {/* Target sites */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <Label className="flex items-center gap-2">
            <Store className="w-4 h-4 text-primary" /> Target sites ({selected.length}/{sites.length})
          </Label>
          <div className="flex gap-2">
            <button
              className="text-xs text-primary hover:underline"
              onClick={() => setSelected(sites.map((s) => s.id))}
              data-testid="select-all-sites"
            >
              Select all
            </button>
            <button
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setSelected([])}
              data-testid="clear-all-sites"
            >
              Clear
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {sites.map((s) => (
            <button
              key={s.id}
              onClick={() => toggleSite(s.id)}
              className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                selected.includes(s.id)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
              data-testid={`target-site-${s.id}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {selected.length === 0 ? (
        <p className="text-sm text-muted-foreground">Select at least one site to begin.</p>
      ) : (
        <Tabs defaultValue="prices">
          <TabsList className="max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="prices" data-testid="master-tab-prices">
              Prices
            </TabsTrigger>
            <TabsTrigger value="sizes" data-testid="master-tab-sizes">
              Price by Size
            </TabsTrigger>
            <TabsTrigger value="expired" data-testid="master-tab-expired">
              Expired Sales
            </TabsTrigger>
            <TabsTrigger value="tagpromo" data-testid="master-tab-tagpromo">
              Promotion Now
            </TabsTrigger>
            <TabsTrigger value="addons" data-testid="master-tab-addons">
              Add-Ons
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="master-tab-scheduled">
              Scheduled
            </TabsTrigger>
          </TabsList>
          <TabsContent value="prices" className="mt-4">
            <PricesPanel siteIds={selected} />
          </TabsContent>
          <TabsContent value="sizes" className="mt-4">
            <SizePricePanel siteIds={selected} />
          </TabsContent>
          <TabsContent value="expired" className="mt-4">
            <ExpiredSalesPanel siteIds={selected} />
          </TabsContent>
          <TabsContent value="tagpromo" className="mt-4">
            <TagPromoPanel siteIds={selected} />
          </TabsContent>
          <TabsContent value="addons" className="mt-4">
            <AddonsPanel siteIds={selected} />
          </TabsContent>
          <TabsContent value="scheduled" className="mt-4">
            <ScheduledPanel siteIds={selected} sites={sites} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/* ---------------- Prices ---------------- */
function priceRange(sitesArr, field) {
  if (field === "sale_price") sitesArr = sitesArr.filter((s) => s.sale_status !== "expired");
  const nums = sitesArr.map((s) => parseFloat(s[field])).filter((n) => !isNaN(n) && n > 0);
  if (!nums.length) return null;
  const min = Math.min(...nums),
    max = Math.max(...nums);
  return min === max ? min.toFixed(2) : `${min.toFixed(2)}–${max.toFixed(2)}`;
}

function PricesPanel({ siteIds }) {
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const pricesReq = useRef(0);
  const load = useCallback(async (refresh = false, quiet = false) => {
    const id = ++pricesReq.current;
    if (!quiet) setLoading(true);
    try {
      const r = await api.get("/catalog", { params: { site_ids: siteIds.join(","), search: search || undefined, refresh: refresh || undefined } });
      if (id !== pricesReq.current) return;
      setCatalog(r.data);
      if (!quiet) setEdits({});
    } catch (e) {
      if (!quiet && id === pricesReq.current) toast.error(apiError(e));
    } finally {
      if (id === pricesReq.current) setLoading(false);
    }
  }, [siteIds, search]);

  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
  }, [load]);

  const quietReload = useCallback(() => load(false, true), [load]);
  usePollWhileLoading(catalog?.loading_sites, quietReload);

  const setEdit = (sku, field, value) =>
    setEdits((e) => ({ ...e, [sku]: { ...e[sku], [field]: value } }));

  const editedItems = useMemo(
    () =>
      Object.entries(edits)
        .map(([sku, v]) => ({
          sku,
          regular_price: v.regular_price,
          sale_price: v.sale_price,
        }))
        .filter((i) => i.regular_price || i.sale_price !== undefined),
    [edits]
  );

  const apply = async () => {
    const byKey = Object.fromEntries((catalog?.skus || []).map((r) => [r.key, r]));
    const items = Object.entries(edits)
      .map(([key, v]) => {
        const row = byKey[key];
        const item = {
          sku: row.sku,
          targets: row.sites.map((s) => ({ site_id: s.site_id, product_id: s.product_id })),
        };
        if (v.regular_price) item.regular_price = v.regular_price;
        if (v.sale_price !== undefined && v.sale_price !== "") item.sale_price = v.sale_price;
        return item;
      })
      .filter((i) => i.regular_price || i.sale_price);
    if (!items.length) return toast.error("Edit at least one price");
    setApplying(true);
    try {
      const r = await api.post("/catalog/apply-prices", { site_ids: siteIds, items });
      toast.success(`Pushed to sites — ${r.data.updated} product updates across ${siteIds.length} sites`);
      if (r.data.missing?.length) toast.warning(`${r.data.missing.length} SKU/site combos not found`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplying(false);
    }
  };

  const dirtyCount = Object.values(edits).filter((v) => v.regular_price || v.sale_price).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="master-price-search"
          />
        </div>
        <Button onClick={apply} disabled={applying || dirtyCount === 0} data-testid="master-apply-prices">
          {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          {`Push${dirtyCount > 0 ? ` ${dirtyCount} change${dirtyCount > 1 ? "s" : ""}` : ""} to ${siteIds.length} site${siteIds.length === 1 ? "" : "s"}`}
        </Button>
      </div>
      <FailedSitesNotice sites={catalog?.failed_sites} />
      <LoadingSitesNotice sites={catalog?.loading_sites} />

      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Current regular</TableHead>
                <TableHead>Current sale</TableHead>
                <TableHead className="w-32">New regular</TableHead>
                <TableHead className="w-32">New sale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading || (!catalog?.skus?.length && catalog?.loading_sites?.length) ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin inline" />
                  </TableCell>
                </TableRow>
              ) : !catalog?.skus?.length ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    No matching SKUs across the selected sites
                  </TableCell>
                </TableRow>
              ) : (
                catalog.skus.map((row) => (
                  <TableRow key={row.key} className="border-border" data-testid={`master-row-${row.key}`}>
                    <TableCell className="font-medium">
                      {row.name}
                      <div className="text-[11px] text-muted-foreground">{row.sites.length} site(s)</div>
                    </TableCell>
                    <TableCell className="font-mono-num text-xs text-muted-foreground">{row.sku}</TableCell>
                    <TableCell className="font-mono-num text-sm">{priceRange(row.sites, "regular_price") || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm text-primary">
                      {priceRange(row.sites, "sale_price") || <span className="text-muted-foreground">—</span>}
                      {row.sites.some((s) => s.sale_status === "expired") && (
                        <div className="text-[10px] text-muted-foreground" data-testid={`master-expired-${row.key}`}>
                          expired sale on {row.sites.filter((s) => s.sale_status === "expired").length} site(s)
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={edits[row.key]?.regular_price ?? ""}
                        onChange={(e) => setEdit(row.key, "regular_price", e.target.value)}
                        placeholder="—"
                        className="h-8 font-mono-num text-sm"
                        data-testid={`master-regular-${row.key}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={edits[row.key]?.sale_price ?? ""}
                        onChange={(e) => setEdit(row.key, "sale_price", e.target.value)}
                        placeholder="—"
                        className="h-8 font-mono-num text-sm"
                        data-testid={`master-sale-${row.key}`}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tag promotion ---------------- */
// Tags + variation promo codes for the chosen stores; keeps polling until every store has loaded.
function useCatalogMeta(siteIds) {
  const [meta, setMeta] = useState({ tags: [], codes: [], loadingSites: [] });
  const reload = useCallback(async () => {
    try {
      const r = await api.get("/catalog", { params: { site_ids: siteIds.join(",") } });
      setMeta({ tags: r.data.all_tags || [], codes: r.data.all_promo_codes || [], loadingSites: r.data.loading_sites || [] });
    } catch (e) {
      setMeta((m) => ({ ...m, loadingSites: m.loadingSites.length ? m.loadingSites : ["your stores"] }));
    }
  }, [siteIds]);
  useEffect(() => {
    reload();
  }, [reload]);
  usePollWhileLoading(meta.loadingSites, reload);
  return meta;
}

const CodeChips = ({ codes, chosen, onToggle, prefix, loading }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${prefix}-code-chips`}>
    {codes.length === 0 && (
      <span className="text-xs text-muted-foreground">
        {loading ? "Loading promo codes from your stores…" : "No promo codes yet — add them in Price by Size"}
      </span>
    )}
    {codes.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onToggle(c)}
        className={`px-2.5 py-1 rounded-md text-xs border font-mono-num transition-colors ${
          chosen.includes(c) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"
        }`}
        data-testid={`${prefix}-code-${c}`}
      >
        {c}
      </button>
    ))}
  </div>
);

function TagPromoPanel({ siteIds }) {
  const { tags, codes, loadingSites } = useCatalogMeta(siteIds);
  const [chosenCodes, setChosenCodes] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [pending, setPending] = useState(null);
  const [type, setType] = useState("percentage");
  const [value, setValue] = useState("15");
  const [busy, setBusy] = useState(false);

  const toggle = (t) => setChosen((c) => (c.includes(t) ? c.filter((x) => x !== t) : [...c, t]));
  const toggleCode = (t) => setChosenCodes((c) => (c.includes(t) ? c.filter((x) => x !== t) : [...c, t]));

  const run = async (clear) => {
    if (!chosen.length && !chosenCodes.length) return toast.error("Pick at least one tag or promo code");
    setBusy(true);
    try {
      const r = await api.post("/catalog/discount-by-tag", {
        site_ids: siteIds,
        tag_names: chosen,
        promo_codes: chosenCodes,
        discount_type: type,
        discount_value: parseFloat(value) || 0,
        clear,
      });
      toast.success(
        clear
          ? `Cleared sale on ${r.data.updated} product${r.data.updated === 1 ? "" : "s"} across ${siteIds.length} site${siteIds.length === 1 ? "" : "s"}`
          : `Applied discount to ${r.data.updated} product${r.data.updated === 1 ? "" : "s"} across ${siteIds.length} site${siteIds.length === 1 ? "" : "s"}`
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">
        Instantly set (or clear) sale prices for every product with the chosen tag(s) and/or every size with the
        chosen promo code(s), on all selected sites. For day/hour schedules use the <strong>Scheduled</strong> tab.
      </p>
      <div className="space-y-2">
        <Label>By variation description code</Label>
        <CodeChips codes={codes} chosen={chosenCodes} onToggle={toggleCode} prefix="master" loading={loadingSites.length > 0} />
        <LoadingSitesNotice sites={loadingSites} />
      </div>
      <div className="space-y-2">
        <Label>Product tags</Label>
        <div className="flex flex-wrap gap-2">
          {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags found</span>}
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => toggle(t)}
              className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                chosen.includes(t)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
              data-testid={`master-tag-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <div className="space-y-2">
          <Label>Discount</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-40" data-testid="master-discount-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage %</SelectItem>
              <SelectItem value="set_price">Set price $</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 flex-1 max-w-[140px]">
          <Label>{type === "percentage" ? "Percent" : "New price"}</Label>
          <Input value={value} onChange={(e) => setValue(e.target.value)} type="number" className="font-mono-num" data-testid="master-discount-value" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button onClick={() => (chosen.length || chosenCodes.length ? setPending("apply") : toast.error("Pick at least one tag or promo code"))} disabled={busy} data-testid="master-apply-tag-discount">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Percent className="w-4 h-4" />}
          Apply across {siteIds.length} site{siteIds.length === 1 ? "" : "s"}
        </Button>
        <Button variant="outline" onClick={() => (chosen.length || chosenCodes.length ? setPending("clear") : toast.error("Pick at least one tag or promo code"))} disabled={busy} data-testid="master-clear-tag-discount">
          <RotateCcw className="w-4 h-4" /> Clear sale
        </Button>
      </div>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending === "clear" ? "Clear sale prices now?" : "Apply this promotion now?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {[...chosen.map((t) => `#${t}`), ...chosenCodes].join(", ")} on {siteIds.length} store{siteIds.length === 1 ? "" : "s"}. This writes to WooCommerce immediately; you can undo it from History.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="promo-now-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const c = pending === "clear"; setPending(null); run(c); }} data-testid="promo-now-confirm">
              {pending === "clear" ? "Clear sale" : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Add-ons ---------------- */
function AddonsPanel({ siteIds }) {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (refresh = false, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get("/catalog/product-addons", { params: { site_ids: siteIds.join(","), refresh: refresh || undefined } });
      setData(r.data);
      if (!quiet) setEdits({});
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [siteIds]);

  useEffect(() => {
    load();
  }, [load]);

  const scanning = data?.scanning?.length > 0;
  useEffect(() => {
    if (!scanning) return undefined;
    const t = setInterval(() => load(false, true), 6000);
    return () => clearInterval(t);
  }, [scanning, load]);

  const key = (g, label) => `${g}|||${label}`;
  const setEdit = (g, label, price) => setEdits((e) => ({ ...e, [key(g, label)]: price }));

  const apply = async (groupTitle, fieldLabel) => {
    const price = edits[key(groupTitle, fieldLabel)];
    if (price === undefined || price === "") return toast.error("Enter a new price");
    setBusy(key(groupTitle, fieldLabel));
    try {
      const r = await api.post("/catalog/product-addons/apply", {
        site_ids: siteIds,
        group_title: groupTitle,
        field_label: fieldLabel,
        price,
      });
      toast.success(`Updated "${fieldLabel}" on ${r.data.updated} product(s) across ${siteIds.length} site(s)`);
      if (r.data.failed) toast.error(`${r.data.failed} could not be updated — try again`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading)
    return (
      <p className="text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin inline" /> Loading add-ons…
      </p>
    );
  const scanNotice = (
    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground mb-3" data-testid="addons-scan-status">
      <span>
        {scanning ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Scanning add-ons on {data.scanning[0]}{data.scanning.length > 1 ? ` (${data.scanning.length - 1} more store${data.scanning.length > 2 ? "s" : ""} queued)` : ""} — one store at a time in the background; results appear automatically.</>
        ) : (
          "Add-on prices are re-scanned from your stores every 6 hours."
        )}
      </span>
      <Button variant="outline" size="sm" disabled={scanning} onClick={() => load(true)} data-testid="addons-rescan">
        Rescan now
      </Button>
    </div>
  );

  if (!data?.groups?.length)
    return scanning ? (
      scanNotice
    ) : (
      <div>
        {scanNotice}
        <p className="text-sm text-muted-foreground">
        No per-product add-ons found. Live sites need Plugin Republic "Product Add-Ons Ultimate" and the site's
        Add-On plugin set to "Plugin Republic" in the Sites tab.
      </p>
      </div>
    );

  const optRange = (instances) => priceRange(instances.map((p) => ({ price: p.price })), "price");

  return (
    <div className="space-y-4">
      {scanNotice}
      <p className="text-sm text-muted-foreground">
        Per-product add-on prices grouped by add-on. Set a new price to update that option on every matching
        product across all {siteIds.length} selected site(s) at once.
      </p>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.groups.map((g) => (
          <div key={g.group_title} className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid={`master-addon-${g.group_title}`}>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-amber-400" />
              <h4 className="font-heading font-semibold">{g.group_title}</h4>
            </div>
            <div className="space-y-2">
              {g.options.map((o) => (
                <div key={o.field_label} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{o.field_label}</div>
                    <div className="text-[10px] text-muted-foreground font-mono-num">
                      now ${optRange(o.instances) || "—"} · {o.instances.length} product(s)
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-sm">$</span>
                    <Input
                      value={edits[key(g.group_title, o.field_label)] ?? ""}
                      onChange={(e) => setEdit(g.group_title, o.field_label, e.target.value)}
                      placeholder="—"
                      className="h-8 w-20 font-mono-num text-sm text-right"
                      data-testid={`master-addon-price-${g.group_title}-${o.field_label}`}
                    />
                    <Button
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      disabled={busy === key(g.group_title, o.field_label)}
                      onClick={() => apply(g.group_title, o.field_label)}
                      data-testid={`master-apply-addon-${g.group_title}-${o.field_label}`}
                    >
                      {busy === key(g.group_title, o.field_label) ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Scheduled cross-site promotions ---------------- */
const emptySched = () => ({
    name: "",
    target_type: "tags",
    tag_names: [],
    tag_ids: [],
    product_ids: [],
    promo_codes: [],
    discount_type: "percentage",
    discount_value: "15",
    free_addons: false,
    message: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    hour_start: 11,
    hour_end: 14,
});

const EDIT_FIELDS = Object.keys(emptySched());

const StorePicker = ({ sites, chosen, onChange }) => (
  <div className="space-y-2" data-testid="sched-edit-stores">
    <Label>Stores</Label>
    <div className="flex flex-wrap gap-2">
      {sites.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(chosen.includes(s.id) ? chosen.filter((x) => x !== s.id) : [...chosen, s.id])}
          className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
            chosen.includes(s.id) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"
          }`}
          data-testid={`sched-edit-store-${s.id}`}
        >
          {s.name}
        </button>
      ))}
    </div>
  </div>
);

function ScheduledPanel({ siteIds, sites }) {
  const [editing, setEditing] = useState(null);
  const { tags, codes, loadingSites } = useCatalogMeta(editing ? editing.site_ids : siteIds);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptySched);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await api.get("/promotions");
      setPromos(p.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter((x) => x !== d) : [...f.days_of_week, d].sort(),
    }));
  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tag_names: f.tag_names.includes(t) ? f.tag_names.filter((x) => x !== t) : [...f.tag_names, t] }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.target_type === "tags" && form.tag_names.length === 0 && form.promo_codes.length === 0 && !(form.tag_ids || []).length)
      return toast.error("Pick at least one tag or promo code");
    const targetIds = editing ? editing.site_ids : siteIds;
    if (targetIds.length === 0) return toast.error("Select at least one site");
    setBusy(true);
    const body = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      hour_start: parseInt(form.hour_start),
      hour_end: parseInt(form.hour_end),
      site_ids: targetIds,
      enabled: editing ? editing.enabled : true,
    };
    try {
      if (editing) {
        await api.put(`/promotions/${editing.id}`, body);
        toast.success(editing.live ? "Saved. Store prices update with the new settings over the next few minutes" : "Promotion updated");
        cancelEdit();
      } else {
        await api.post("/promotions", body);
        toast.success(`Scheduled on ${targetIds.length} site${targetIds.length > 1 ? "s" : ""}`);
        setForm((f) => ({ ...f, name: "", message: "" }));
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (p) => {
    const f = emptySched();
    EDIT_FIELDS.forEach((k) => {
      if (p[k] !== undefined && p[k] !== null) f[k] = p[k];
    });
    setForm({ ...f, discount_value: String(p.discount_value) });
    setEditing({ id: p.id, site_ids: p.site_ids || [p.site_id], enabled: p.enabled, live: !!p.active });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm(emptySched());
  };

  const toggle = async (id) => {
    try {
      await api.post(`/promotions/${id}/toggle`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/promotions/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-lg border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-primary" />
          <h4 className="font-heading font-semibold" data-testid="sched-form-title">{editing ? "Edit scheduled promotion" : "New scheduled promotion"}</h4>
        </div>
        {editing ? (
          <>
            {editing.live && (
              <p className="text-xs text-amber-400" data-testid="sched-edit-live-note">
                This promo is live. Saving puts the original prices back, then re-applies it with your changes within a few minutes.
              </p>
            )}
            <StorePicker sites={sites} chosen={editing.site_ids} onChange={(ids) => setEditing({ ...editing, site_ids: ids })} />
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Runs automatically on all {siteIds.length} selected site(s), each in its own timezone. Sale prices turn on
            and off by date, weekday and hour. Checked every 15 minutes.
          </p>
        )}
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Weekend Lunch Deal" data-testid="sched-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Applies to</Label>
            <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
              <SelectTrigger data-testid="sched-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tags">By tag and/or promo code</SelectItem>
                <SelectItem value="all">All products</SelectItem>
                {form.target_type === "products" && (
                  <SelectItem value="products">Specific products ({form.product_ids.length})</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Discount</Label>
            <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
              <SelectTrigger data-testid="sched-discount-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage %</SelectItem>
                <SelectItem value="set_price">Set price $</SelectItem>
                {form.discount_type === "fixed" && <SelectItem value="fixed">Amount off $</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        </div>
        <PromoMessageInput value={form.message} onChange={(v) => setForm({ ...form, message: v })} testId="sched-message-input" />
        <label className="flex items-center gap-3 text-sm" data-testid="sched-free-addons">
          <Switch checked={form.free_addons} onCheckedChange={(v) => setForm({ ...form, free_addons: v })} data-testid="sched-free-addons-switch" />
          <span>
            Make half &amp; half upcharges free on promo sizes
            <span className="block text-xs text-muted-foreground">
              Half &amp; half choices for the promo size are $0 while the promo is live, then restored. Extra toppings, crust and sauces keep their regular price.
            </span>
          </span>
        </label>
        {form.target_type === "tags" && (
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2">
              {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags found</span>}
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                    form.tag_names.includes(t)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                  data-testid={`sched-tag-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <Label>By variation description code</Label>
            <CodeChips
              codes={codes}
              loading={loadingSites.length > 0}
              chosen={form.promo_codes}
              prefix="sched"
              onToggle={(c) =>
                setForm((f) => ({ ...f, promo_codes: f.promo_codes.includes(c) ? f.promo_codes.filter((x) => x !== c) : [...f.promo_codes, c] }))
              }
            />
          </div>
        )}
        <div className="space-y-2">
          <Label>{form.discount_type === "percentage" ? "Discount %" : form.discount_type === "fixed" ? "Amount off $" : "New sale price $"}</Label>
          <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="font-mono-num" data-testid="sched-value" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sched-start" />
          </div>
          <div className="space-y-2">
            <Label>End date</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sched-end" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Active days</Label>
          <div className="flex gap-1.5">
            {DAYS.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(i)}
                className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                  form.days_of_week.includes(i)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
                data-testid={`sched-day-${i}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Start hour</Label>
            <Select value={String(form.hour_start)} onValueChange={(v) => setForm({ ...form, hour_start: v })}>
              <SelectTrigger data-testid="sched-hour-start"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOURS.slice(0, 24).map((h) => (
                  <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>End hour</Label>
            <Select value={String(form.hour_end)} onValueChange={(v) => setForm({ ...form, hour_end: v })}>
              <SelectTrigger data-testid="sched-hour-end"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOURS.slice(1).map((h) => (
                  <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <Button onClick={create} disabled={busy || editing.site_ids.length === 0} className="flex-1" data-testid="sched-save-edit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
              Save changes ({editing.site_ids.length} store{editing.site_ids.length === 1 ? "" : "s"})
            </Button>
            <Button variant="outline" onClick={cancelEdit} data-testid="sched-cancel-edit">Cancel</Button>
          </div>
        ) : (
          <Button onClick={create} disabled={busy || siteIds.length === 0} className="w-full" data-testid="sched-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create on {siteIds.length} site{siteIds.length > 1 ? "s" : ""}
          </Button>
        )}
      </div>

      <div className="space-y-3">
        <h4 className="font-heading font-semibold">Existing promotions</h4>
        {loading ? (
          <p className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</p>
        ) : promos.length === 0 ? (
          <p className="text-sm text-muted-foreground">None yet.</p>
        ) : (
          promos.map((p) => (
            <div key={p.id} className="rounded-lg border border-border bg-card p-4 space-y-2" data-testid={`sched-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h5 className="font-heading font-semibold">{p.name}</h5>
                  <p className="text-sm text-primary font-mono-num">
                    {p.discount_type === "percentage"
                      ? `-${p.discount_value}%`
                      : p.discount_type === "set_price"
                      ? `→ $${p.discount_value}`
                      : `-$${p.discount_value}`}
                    {p.free_addons && <span className="ml-2 text-xs" data-testid={`sched-free-addons-badge-${p.id}`}>+ free half &amp; half</span>}
                    {p.active && <span className="ml-2 text-emerald-400">● live</span>}
                  </p>
                </div>
                <Switch checked={p.enabled} onCheckedChange={() => toggle(p.id)} data-testid={`sched-toggle-${p.id}`} />
              </div>
              <div className="flex flex-wrap gap-1 items-center" data-testid={`sched-stores-${p.id}`}>
                <span className="text-xs text-muted-foreground">Stores:</span>
                {(p.site_names || []).map((n) => (
                  <Badge key={n} variant="secondary" className="text-[10px]">{n}</Badge>
                ))}
              </div>
              {p.message && (
                <p className="text-xs italic" data-testid={`sched-message-${p.id}`}>Menu message: “{p.message}”</p>
              )}
              <NeverRunsWarning p={p} />
              {(p.tag_names?.length > 0 || p.promo_codes?.length > 0) && (
                <p className="text-xs text-muted-foreground" data-testid={`sched-targets-${p.id}`}>
                  {[...(p.tag_names || []).map((t) => `#${t}`), ...(p.promo_codes || [])].join(" · ")}
                </p>
              )}
              <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono-num">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.start_date}→{p.end_date}</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {String(p.hour_start).padStart(2, "0")}:00–{String(p.hour_end).padStart(2, "0")}:00
                </span>
              </div>
              <div className="flex gap-0.5">
                {DAYS.map((d, i) => (
                  <span key={d} className={`text-[10px] px-1 rounded ${(p.days_of_week || []).includes(i) ? "bg-primary/20 text-primary" : "text-muted-foreground/40"}`}>
                    {d[0]}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`sched-edit-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(p.id)} data-testid={`sched-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
